import { normalizeItemNotas } from '../utils/formatters';
import {
    calcularFechaVencimDesdeVtoComprob,
    resolveFechaVencimForSave,
    toDateOnly,
    toFechaVencimISO,
    validarFechasFacturaVenta,
} from '../utils/facturaVentaFechas';
import {
    resolveFechaVencimFromApi,
    resolveVtoComprobComprobanteFromApi,
} from '../utils/facturaVentaVencim';
import api from './api';
import clienteService from './clienteService';
import productoService from './productoService';
import transporteService from './transporteService';
import movMonedaService from './movMonedaService';
import {
    pedidoTieneLineasConPrecioCero,
    precioListaDetallePedido,
    prepararDetallesParaCreateConPrecioCero,
} from '../utils/pedidoVentaLineas';
import { buildVentaSearchQueryParams, VentaListSearchParamsBase } from '../utils/ventaSearchQueryParams';
import { estadoDesdeRegistroPedido, normalizeEstadoPedidoMaster } from '../utils/pedidoVentaEstados';
const DEFAULT_NRO_SUC = 1;

export interface PedidoVentaDetalle {
    nroIdformulario: number;
    nroItem: number;
    nroFormularioMaster?: number;
    nroItemMaster?: number;
    codigoArticulo?: string;
    descripcionArticulo?: string;
    cantidad?: number;
    precioUnitario?: number;
    precioLista?: number;
    descuento?: number;
    porcDesc?: number;
    subtotal?: number;
    /** Precio unitario con IVA (consumidor final). */
    precioUnitarioFinal?: number;
    /** Subtotal con IVA (consumidor final). */
    subtotalFinal?: number;
    notas?: string;
    um?: string; // Unidad de medida
    codigoMoneda?: number; // Código de moneda
    cotizacion?: number; // Cotización
}

export interface PedidoVenta {
    nroFormulario: number;
    nroTipoFormulario: string;
    tipoCliente: string;
    fechaEmision: Date;
    fechaVencim?: string | Date;
    vtoComprob?: number | null;
    horaEmision: Date;
    nroCliente: number;
    nombre: string;
    nroVendedor: number;
    nroLista: string;
    codigoMoneda: string;
    importeTotal: number;
    importeSinIva?: number;
    ivainscripto?: number;
    asentado: string;
    descripcion: string;
    usuario: string;
    detalles: PedidoVentaDetalle[];
    porcDesc?: number | null;
    porcDesc2?: number | null;
    porcDesc3?: number | null;
    facturado?: string;
    nroFormularioFactura?: number;
    integrar?: number;
    estado?: string; // Estado del pedido: 'Nuevo', 'Preparado', 'Impreso', 'Facturado', 'Anulado', 'Listo para pickear', etc.
    tipoIVA?: string;
    tipoIva?: string;
}

export interface PedidoVentaSearchParams extends VentaListSearchParamsBase {
    PendienteFacturar?: boolean;
    facturado?: string;
    nroFormularioFactura?: number;
}

export interface PedidoVentaPagedResult {
    items: PedidoVenta[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

const withPedidoNotasTrimmed = (data: PedidoVenta): PedidoVenta => ({
    ...data,
    detalles: (data.detalles || []).map((d) => {
        const raw = d as PedidoVentaDetalle & { Notas?: string | null; Um?: string | null; UM?: string | null };
        return {
            ...d,
            notas: normalizeItemNotas(raw.notas ?? raw.Notas),
            um: String(raw.um ?? raw.Um ?? raw.UM ?? '').trim() || undefined,
        };
    }),
});

const normalizePedidoVentaFromApi = (raw: any): PedidoVenta => {
    const fechaVencimIso = resolveFechaVencimFromApi(raw);
    const vtoComprob = resolveVtoComprobComprobanteFromApi(raw);
    const base = withPedidoNotasTrimmed({
        ...(raw as PedidoVenta),
        detalles: raw?.detalles ?? raw?.Detalles ?? [],
    });
    const estadoRaw = estadoDesdeRegistroPedido(raw as Record<string, unknown>);
    return {
        ...base,
        ...(fechaVencimIso != null ? { fechaVencim: fechaVencimIso } : {}),
        ...(vtoComprob != null ? { vtoComprob } : {}),
        ...(estadoRaw ? { estado: normalizeEstadoPedidoMaster(estadoRaw) } : {}),
    };
};

const round5 = (n: number) => Math.round(n * 100000) / 100000;

const toPrecioItem = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const resolverPrecioListaCreateItem = (
    detalle: PedidoVentaDetalle,
    productoData?: any,
    cotizacionUSD?: number | null,
): number => {
    if (detalle.precioUnitario != null && Number.isFinite(Number(detalle.precioUnitario))) {
        return precioListaDetallePedido(detalle);
    }

    const precioProducto = productoData?.precioVenta ?? 0;
    if (precioProducto && precioProducto > 0) {
        const tipoMonedaProd = productoData?.tipoMoneda ?? productoData?.tipoMonedaArticulo ?? 0;
        if (Number(tipoMonedaProd) === 1 && cotizacionUSD && cotizacionUSD > 0) {
            return Number((precioProducto * cotizacionUSD).toFixed(2));
        }
        return Number(precioProducto);
    }
    return 0;
};

const extraerNroFormularioPedido = (data: any): number | null => {
    const candidatos = [
        data?.nroFormulario,
        data?.formulario?.nroFormulario,
        data?.data?.nroFormulario,
    ];
    for (const value of candidatos) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
};

const resolveMasterNumber = (detalle: any, keys: string[]): number | undefined => {
    for (const key of keys) {
        const value = detalle?.[key];
        if (value === undefined || value === null || value === '') {
            continue;
        }
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return undefined;
};

const pedidoVentaService = {
    getPedidosVenta: async (params: PedidoVentaSearchParams): Promise<PedidoVentaPagedResult> => {
        const queryParams = buildVentaSearchQueryParams(params);
        if (params.facturado) {
            queryParams.Facturado = params.facturado;
        }
        if (params.nroFormularioFactura != null && params.nroFormularioFactura > 0) {
            queryParams.NroFormularioFactura = params.nroFormularioFactura;
        }
        if (params.PendienteFacturar) {
            queryParams.PendienteFacturar = true;
        }
        const response = await api.get<PedidoVentaPagedResult>('/api/PedidosVenta', { params: queryParams });
        return {
            ...response.data,
            items: (response.data.items || []).map((item) => normalizePedidoVentaFromApi(item)),
        };
    },

    getPedidoVenta: async (id: number, opts?: { sinCache?: boolean }): Promise<PedidoVenta> => {
        const response = await api.get<PedidoVenta>(`/api/pedidosventa/${id}`, {
            params: opts?.sinCache ? { _: Date.now() } : undefined,
        });
        return normalizePedidoVentaFromApi(response.data);
    },

    createPedidoVenta: async (pedidoVenta: PedidoVenta & { 
        nroSuc?: number; 
        ordenCompra?: string; 
        transporte?: string;
        ubicacion?: string;
        formaDePago?: string;
        idCompania?: string;
    }): Promise<PedidoVenta> => {
        // Validaciones de datos obligatorios
        const errors: string[] = [];

        // Validar cliente
        if (!pedidoVenta.nroCliente || pedidoVenta.nroCliente <= 0) {
            errors.push('nroCliente es obligatorio y debe ser mayor a 0');
        }

        // Obtener datos del cliente antes de crear el payload
        let clienteData: any = null;
        if (pedidoVenta.nroCliente && pedidoVenta.nroCliente > 0) {
            try {
                clienteData = await clienteService.getCliente(pedidoVenta.nroCliente);
                if (!clienteData) {
                    errors.push(`No se encontró el cliente con número ${pedidoVenta.nroCliente}`);
                }
            } catch (error: any) {
                const mensajeError = error?.response?.status === 404 
                    ? `No se encontró el cliente con número ${pedidoVenta.nroCliente}`
                    : `Error al obtener los datos del cliente: ${error?.message || 'Error desconocido'}`;
                errors.push(mensajeError);
            }
        }

        // Validar que haya al menos un detalle
        if (!pedidoVenta.detalles || pedidoVenta.detalles.length === 0) {
            errors.push('Debe haber al menos un item en el pedido');
        }

        // Validar cada detalle y obtener datos de productos
        const productosData: Record<string, any> = {};
        if (pedidoVenta.detalles && pedidoVenta.detalles.length > 0) {
            // Obtener datos completos de cada producto
            for (const detalle of pedidoVenta.detalles) {
                if (detalle.codigoArticulo) {
                    try {
                        const producto = await productoService.getProducto(detalle.codigoArticulo);
                        if (!producto) {
                            errors.push(`No se encontró el producto con código "${detalle.codigoArticulo}"`);
                        } else {
                            productosData[detalle.codigoArticulo] = producto;
                        }
                    } catch (error: any) {
                        const itemIndex = pedidoVenta.detalles.findIndex(d => d.codigoArticulo === detalle.codigoArticulo) + 1;
                        const mensajeError = error?.response?.status === 404
                            ? `Item ${itemIndex}: No se encontró el producto con código "${detalle.codigoArticulo}"`
                            : `Item ${itemIndex}: Error al obtener el producto "${detalle.codigoArticulo}": ${error?.message || 'Error desconocido'}`;
                        errors.push(mensajeError);
                    }
                }
            }

            pedidoVenta.detalles.forEach((detalle, index) => {
                const itemNum = index + 1;
                // Validar codigoArticulo
                if (!detalle.codigoArticulo || String(detalle.codigoArticulo).trim() === '') {
                    errors.push(`Item ${itemNum}: codigoArticulo es obligatorio`);
                }
                // Validar descripcionArticulo
                if (!detalle.descripcionArticulo || String(detalle.descripcionArticulo).trim() === '') {
                    errors.push(`Item ${itemNum}: descripcion es obligatoria`);
                }
                // Validar cantidad
                if (detalle.cantidad === undefined || detalle.cantidad === null) {
                    errors.push(`Item ${itemNum}: cantidad es obligatoria`);
                } else if (typeof detalle.cantidad !== 'number' || isNaN(detalle.cantidad)) {
                    errors.push(`Item ${itemNum}: cantidad debe ser un número válido (tipo: ${typeof detalle.cantidad}, valor: ${detalle.cantidad})`);
                } else if (detalle.cantidad <= 0) {
                    errors.push(`Item ${itemNum}: cantidad debe ser mayor a 0 (valor actual: ${detalle.cantidad})`);
                }
            });
        }

        // Validar fechas
        let fechaEmisionDate = new Date();
        if (!pedidoVenta.fechaEmision) {
            errors.push('La Fecha de Emisión es obligatoria. Por favor, ingrese la fecha en la sección "Información del Pedido"');
        } else {
            fechaEmisionDate = pedidoVenta.fechaEmision instanceof Date
                ? pedidoVenta.fechaEmision
                : new Date(pedidoVenta.fechaEmision);
            if (isNaN(fechaEmisionDate.getTime())) {
                errors.push('La Fecha de Emisión debe ser una fecha válida');
            }
        }

        const fechaVencimDate = resolveFechaVencimForSave(fechaEmisionDate, pedidoVenta.fechaVencim);
        const fechaVencimError = validarFechasFacturaVenta(
            fechaEmisionDate,
            fechaVencimDate,
            calcularFechaVencimDesdeVtoComprob(fechaEmisionDate, pedidoVenta.vtoComprob),
        );
        if (fechaVencimError) {
            errors.push(fechaVencimError);
        }

        // Validar tipos de datos numéricos
        if (pedidoVenta.nroCliente !== undefined && (typeof pedidoVenta.nroCliente !== 'number' || isNaN(pedidoVenta.nroCliente))) {
            errors.push('nroCliente debe ser un número válido');
        }

        if ((pedidoVenta as any).nroSuc !== undefined && (pedidoVenta as any).nroSuc !== null) {
            if (typeof (pedidoVenta as any).nroSuc !== 'number' || isNaN((pedidoVenta as any).nroSuc)) {
                errors.push('nroSuc debe ser un número válido');
            }
        }

        // Si hay errores, lanzarlos con mensaje claro
        if (errors.length > 0) {
            const errorMessage = errors.length === 1 
                ? errors[0]
                : `Se encontraron ${errors.length} errores:\n${errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`;
            console.error('❌ Errores de validación:', errors);
            throw new Error(errorMessage);
        }

        // Obtener la cotización USD solo si falta precio en el detalle y hay que usar el catálogo
        let cotizacionUSD: number | null = null;
        const requiereFallbackCatalogo = pedidoVenta.detalles.some(
            (d) => d.precioUnitario == null || !Number.isFinite(Number(d.precioUnitario)),
        );
        if (requiereFallbackCatalogo) {
            try {
                const cotizacion = await movMonedaService.getUltimaCotizacion();
                if (cotizacion && Number.isFinite(cotizacion) && cotizacion > 0) {
                    cotizacionUSD = cotizacion;
                }
            } catch (error) {
                console.warn('⚠️ No se pudo obtener cotización USD para fallback:', error);
            }
        }

        // Mapear los detalles a la estructura de items esperada por el endpoint
        // IMPORTANTE: El precio del detalle ya viene convertido a ARS (con cotización aplicada si era USD)
        // El precio 0 explícito se respeta; solo se usa catálogo si el detalle no trae precio
        const requiereActualizacionPorPrecioCero = pedidoTieneLineasConPrecioCero(pedidoVenta.detalles);
        const detallesParaCreate = requiereActualizacionPorPrecioCero
            ? prepararDetallesParaCreateConPrecioCero(pedidoVenta.detalles ?? [])
            : (pedidoVenta.detalles ?? []);

        const items = detallesParaCreate.map((detalle) => {
            const productoData = productosData[detalle.codigoArticulo || ''];
            const precioLista = resolverPrecioListaCreateItem(detalle, productoData, cotizacionUSD);
            const porcDesc = detalle.descuento ?? detalle.porcDesc ?? productoData?.porc_Desc ?? productoData?.porcDesc ?? 0;

            // Como el precio ya está convertido a ARS, siempre se envía codigoMoneda: 0 y cotizacion: 0
            const codigoMoneda = 0;
            const cotizacion = 0;

            const nroFormularioMaster = resolveMasterNumber(detalle as any, [
                'nroFormularioMaster',
                'nroformulariomaster',
                'NroFormularioMaster',
                'nro_formulario_master',
            ]);
            const nroItemMaster = resolveMasterNumber(detalle as any, [
                'nroItemMaster',
                'nroitemmaster',
                'NroItemMaster',
                'nro_item_master',
            ]);

            return {
                codigoArticulo: String(detalle.codigoArticulo ?? '').trim(),
                descripcion: String(detalle.descripcionArticulo ?? productoData?.descripcion ?? '').trim(),
                cantidad: Number(detalle.cantidad ?? 0.01),
                precioUnitario: toPrecioItem(precioLista),
                porcDesc: toPrecioItem(porcDesc),
                nroFormularioMaster,
                nroItemMaster,
                um: detalle.um ?? '',
                codigoMoneda: Number(codigoMoneda),
                cotizacion: Number(cotizacion),
                notas: normalizeItemNotas(detalle.notas),
            };
        });

        // Extraer nroUbicacion del campo ubicación (formato: "NOMBRE (número)")
        // El endpoint espera nroUbicacionOrigen como número (int), no el nombre
        let nroUbicacionOrigen = 0;
        if ((pedidoVenta as any).ubicacion) {
            try {
                const ubicacionStr = String((pedidoVenta as any).ubicacion);
                const match = ubicacionStr.match(/\((\d+)\)/);
                if (match && match[1]) {
                    nroUbicacionOrigen = Number(match[1]);
                }
            } catch (error) {
                // Si hay error, dejar en 0
                nroUbicacionOrigen = 0;
            }
        }

        // Obtener el valor de formaEntrega del transporte seleccionado
        // El valor debe ser el campo formaEntrega del Transporte obtenido mediante GET api/Transportes/{nroExpreso}
        // Por ejemplo: si se selecciona PROPIO (nroExpreso=1), se hace GET api/Transportes/1
        // y el valor de formaEntrega será el valor del campo formaEntrega del objeto Transporte (puede ser null)
        let formaEntrega: string | null = null;
        const transporteSeleccionado = String((pedidoVenta as any).transporte ?? '').trim();
        if (transporteSeleccionado) {
            try {
                // Obtener el transporte específico mediante GET api/Transportes/{nroExpreso}
                const transporteEncontrado = await transporteService.getTransporte(transporteSeleccionado);
                
                if (transporteEncontrado) {
                    // Usar el campo formaEntrega del transporte obtenido
                    // Puede ser null, string, o undefined
                    const formaEntregaRaw = transporteEncontrado.formaEntrega ?? (transporteEncontrado as any).formaEntrega;
                    
                    if (formaEntregaRaw !== null && formaEntregaRaw !== undefined) {
                        // Si tiene valor, convertirlo a string
                        formaEntrega = String(formaEntregaRaw).trim() || null;
                    } else {
                        // Si es null o undefined, dejar null
                        formaEntrega = null;
                    }
                    
                    console.log('🔍 Transporte obtenido mediante GET api/Transportes/{nroExpreso}:', transporteSeleccionado);
                    console.log('🔍 formaEntrega del transporte:', formaEntregaRaw);
                    console.log('📤 formaEntrega a enviar:', formaEntrega);
                } else {
                    console.warn('⚠️ No se encontró el transporte con nroExpreso:', transporteSeleccionado);
                }
            } catch (error) {
                console.error('❌ Error al obtener transporte mediante GET api/Transportes/{nroExpreso}:', error);
                // Si hay error, dejar null
                formaEntrega = null;
            }
        }

        const fechaEmisionISO = toFechaVencimISO(fechaEmisionDate);
        const fechaVencimISO = toFechaVencimISO(fechaVencimDate);

        const getPorcentajeBonificacion = (...values: Array<number | string | null | undefined>) => {
            for (const value of values) {
                if (value === null || value === undefined) continue;
                const num = Number(value);
                if (Number.isFinite(num)) {
                    return Math.max(0, Math.min(100, num));
                }
            }
            return 0;
        };

        const porcDescValue = getPorcentajeBonificacion(
            (pedidoVenta as any).porcDesc,
            clienteData?.porcDesc,
            (clienteData as any)?.porc_Desc
        );
        const porcDesc2Value = getPorcentajeBonificacion(
            (pedidoVenta as any).porcDesc2,
            clienteData?.porcDesc2,
            (clienteData as any)?.porc_Desc2
        );
        const porcDesc3Value = getPorcentajeBonificacion(
            (pedidoVenta as any).porcDesc3,
            clienteData?.porcDesc3,
            (clienteData as any)?.porc_Desc3
        );

        // Construir el objeto formulario según la estructura del endpoint
        const nroSucValueRaw = (pedidoVenta as any).nroSuc;
        const nroSucValue = Number.isFinite(Number(nroSucValueRaw)) && Number(nroSucValueRaw) > 0
            ? Number(nroSucValueRaw)
            : DEFAULT_NRO_SUC;

        const tipoIvaValue = (pedidoVenta as any).tipoIVA ?? (pedidoVenta as any).tipoIva ?? clienteData?.tipoIva ?? '';

        const nroVendedorPedido = Number(pedidoVenta.nroVendedor ?? 0);

        const formulario: any = {
            descripcion: String(pedidoVenta.descripcion ?? '').trim(), // Del campo Descripción
            nroSuc: nroSucValue, // Desde el formulario (fallback: 1)
            fechaEmision: fechaEmisionISO, // Del campo Fecha de Emisión
            fechaVencim: fechaVencimISO,
            nroCliente: Number(clienteData?.nroCliente ?? pedidoVenta.nroCliente ?? 0), // Del cliente obtenido
            ...(Number.isFinite(nroVendedorPedido) && nroVendedorPedido > 0 ? { nroVendedor: nroVendedorPedido } : {}),
            nroLista: String(clienteData?.nroLista ?? pedidoVenta.nroLista ?? '').trim(), // Del cliente obtenido
            formaDePago: String((pedidoVenta as any).formaDePago ?? '').trim(), // De la sección Forma de Pago
            nroUbicacionOrigen: Number(nroUbicacionOrigen), // nroUbicacion extraído de la ubicación seleccionada (número)
            ordenDeCompra: String((pedidoVenta as any).ordenCompra ?? '').trim(), // Del campo Orden de Compra
            codigoDireccion: '', // Vacío por ahora
            codigoDireccionEnvio: '', // Vacío por ahora
            direccionEnvio: '', // Vacío por ahora
            formaEntrega: formaEntrega ?? '', // Valor del campo formaEntrega del transporte seleccionado (puede ser null, se envía como string vacío)
            porcDesc: porcDescValue,
            porcDesc2: porcDesc2Value,
            porcDesc3: porcDesc3Value
        };

        if (tipoIvaValue && String(tipoIvaValue).trim() !== '') {
            formulario.tipoIva = String(tipoIvaValue).trim();
        }

        const nombrePedido = String(pedidoVenta.nombre ?? '').trim();
        if (nombrePedido) {
            formulario.nombre = nombrePedido;
        }
        
        // Determinar el valor a enviar en cuit
        // Si es consumidor final, enviar dni; en caso contrario, enviar cuit
        const esConsumidorFinal = tipoIvaValue === 'C' || tipoIvaValue === 'CF';
        const valorCuit = esConsumidorFinal 
            ? ((pedidoVenta as any).dni || (clienteData as any)?.dni || '')
            : ((pedidoVenta as any).cuit || clienteData?.cuit || '');
        
        // Agregar cuit al formulario solo si tiene un valor válido
        if (valorCuit && String(valorCuit).trim() !== '') {
            formulario.cuit = String(valorCuit).trim();
        }
        
        // Solo incluir codigoCiudadEnvio si tiene un valor válido (no 0 y no vacío)
        // Si no tiene valor, no incluir el campo en el payload
        const codigoCiudadEnvioValue = (pedidoVenta as any).codigoCiudadEnvio;
        if (codigoCiudadEnvioValue !== undefined && codigoCiudadEnvioValue !== null && codigoCiudadEnvioValue !== 0) {
            formulario.codigoCiudadEnvio = Number(codigoCiudadEnvioValue);
        }

        // Solo incluir idCompania si tiene un valor válido
        const idCompaniaValue = (pedidoVenta as any).idCompania;
        if (idCompaniaValue !== undefined && idCompaniaValue !== null && String(idCompaniaValue).trim() !== '') {
            formulario.idCompania = String(idCompaniaValue).trim();
        }

        // Incluir nroFormularioCotizacion si el pedido proviene de Preparar Pedido (para marcar cotización como facturada)
        const nroFormularioCotizacionValue = (pedidoVenta as any).nroFormularioCotizacion;
        if (nroFormularioCotizacionValue !== undefined && nroFormularioCotizacionValue !== null && Number(nroFormularioCotizacionValue) > 0) {
            formulario.nroFormularioCotizacion = Number(nroFormularioCotizacionValue);
        }

        // Preparar el payload según la especificación del endpoint
        const payload = {
            formulario,
            items
        };

        // Usar el mismo endpoint que el GET: /api/PedidosVenta
        const endpoint = '/api/PedidosVenta';
        
        // Log detallado para depuración
        console.log('📤 ===== INICIO PETICIÓN POST =====');
        console.log('📤 Endpoint:', endpoint);
        console.log('📤 Base URL:', api.defaults.baseURL);
        console.log('📤 Método: POST');
        console.log('📤 Payload completo:', JSON.stringify(payload, null, 2));
        console.log('📤 URL completa será:', `${api.defaults.baseURL}${endpoint}`);
        console.log('📤 ===== FIN LOG PRE-ENVÍO =====');

        try {
            // El interceptor de axios ya agrega automáticamente el token y Content-Type
            const response = await api.post<PedidoVenta>(endpoint, payload);
            
            // Log de respuesta exitosa
            console.log(`✅ ===== RESPUESTA EXITOSA =====`);
            console.log('✅ Endpoint usado:', endpoint);
            console.log('✅ Status:', response.status);
            console.log('✅ Data:', response.data);
            console.log('✅ ===== FIN RESPUESTA =====');

            if (requiereActualizacionPorPrecioCero) {
                const nroFormulario = extraerNroFormularioPedido(response.data);
                if (!nroFormulario) {
                    throw new Error('El pedido se creó pero no se pudo obtener su número para guardar líneas con precio cero.');
                }
                await pedidoVentaService.updatePedidoVenta(nroFormulario, pedidoVenta);
                return pedidoVentaService.getPedidoVenta(nroFormulario);
            }
            
            return withPedidoNotasTrimmed(response.data);
        } catch (error: any) {
            console.error('❌ ===== ERROR EN PETICIÓN POST =====');
            console.error('❌ Endpoint:', endpoint);
            console.error('❌ Status:', error?.response?.status);
            console.error('❌ Status Text:', error?.response?.statusText);
            console.error('❌ URL que se intentó:', error?.config?.url);
            console.error('❌ URL completa:', `${error?.config?.baseURL || api.defaults.baseURL}${error?.config?.url}`);
            console.error('❌ Response Data:', error?.response?.data);
            console.error('❌ Request Data enviada:', error?.config?.data);
            console.error('❌ Headers enviados:', error?.config?.headers);
            console.error('❌ ===== FIN ERROR =====');
            
            throw error;
        }
    },

    updatePedidoVenta: async (id: number, pedidoVenta: PedidoVenta & { 
        nroSuc?: number; 
        ordenCompra?: string; 
        transporte?: string;
        ubicacion?: string;
        formaDePago?: string;
        idCompania?: string;
    }): Promise<PedidoVenta> => {
        // El PUT debe usar la misma estructura que devuelve el GET (campos planos con detalles)
        // NO usar la estructura de creación (formulario + items)
        
        // Preparar fechas (misma normalización que en POST)
        const fechaEmisionDate = toDateOnly(pedidoVenta.fechaEmision) ?? new Date();
        const fechaEmisionISO = toFechaVencimISO(fechaEmisionDate);

        const fechaVencimDate = resolveFechaVencimForSave(fechaEmisionDate, pedidoVenta.fechaVencim);
        const fechaVencimError = validarFechasFacturaVenta(
            fechaEmisionDate,
            fechaVencimDate,
            calcularFechaVencimDesdeVtoComprob(fechaEmisionDate, (pedidoVenta as PedidoVenta).vtoComprob),
        );
        if (fechaVencimError) {
            throw new Error(fechaVencimError);
        }
        const fechaVencimISO = toFechaVencimISO(fechaVencimDate);

        // Extraer nroUbicacionOrigen de ubicación (formato: "NOMBRE (número)")
        let nroUbicacionOrigen: number | undefined = undefined;
        if ((pedidoVenta as any).ubicacion) {
            try {
                const ubicacionStr = String((pedidoVenta as any).ubicacion);
                const match = ubicacionStr.match(/\((\d+)\)/);
                if (match && match[1]) {
                    nroUbicacionOrigen = Number(match[1]);
                }
            } catch (error) {
                console.warn('Error al extraer nroUbicacionOrigen:', error);
            }
        }

        // Preparar detalles asegurando que tengan nroIdformulario igual al id
        // NO incluir subtotal ya que el backend lo calcula
        // El campo descuento debe ser el MONTO DESCONTADO UNITARIO (por unidad), no el porcentaje
        // El precioUnitario debe ser el precio de lista MENOS el descuento unitario
        // Los montos unitarios deben redondearse a 5 decimales, no a 2
        const detalles = (pedidoVenta.detalles || []).map((detalle, index) => {
            const precioLista = precioListaDetallePedido(detalle);
            const descuentoPorcentaje = Number(detalle.descuento ?? detalle.porcDesc ?? 0);
            const descuentoMontoUnitario = precioLista * (descuentoPorcentaje / 100);
            const precioUnitarioConDescuento = precioLista - descuentoMontoUnitario;
            
            // Construir objeto sin incluir subtotal explícitamente
            const nroFormularioMaster = resolveMasterNumber(detalle as any, [
                'nroFormularioMaster',
                'nroformulariomaster',
                'NroFormularioMaster',
                'nro_formulario_master',
            ]);
            const nroItemMaster = resolveMasterNumber(detalle as any, [
                'nroItemMaster',
                'nroitemmaster',
                'NroItemMaster',
                'nro_item_master',
            ]);

            const detallePayload: any = {
                nroIdformulario: id,
                nroItem: detalle.nroItem || (index + 1),
                nroFormularioMaster,
                nroItemMaster,
                codigoArticulo: detalle.codigoArticulo || '',
                descripcionArticulo: detalle.descripcionArticulo || '',
                cantidad: detalle.cantidad || 0,
                precioLista: round5(precioLista),
                precioUnitario: round5(precioUnitarioConDescuento),
                descuento: round5(descuentoMontoUnitario),
                notas: normalizeItemNotas(detalle.notas),
            };
            
            // Incluir UM siempre (también vacía) para no perderla en updates
            detallePayload.um = String(detalle.um ?? '').trim();
            if (detalle.codigoMoneda !== undefined && detalle.codigoMoneda !== null) {
                detallePayload.codigoMoneda = detalle.codigoMoneda;
            }
            if (detalle.cotizacion !== undefined && detalle.cotizacion !== null) {
                detallePayload.cotizacion = detalle.cotizacion;
            }
            if (detalle.porcDesc !== undefined && detalle.porcDesc !== null) {
                detallePayload.porcDesc = detalle.porcDesc;
            }
            
            // NO incluir subtotal - el backend lo calcula
            
            return detallePayload;
        });

        // Construir payload con estructura plana (como el GET)
        const payload: any = {
            nroFormulario: id, // Puede coincidir con el id de la URL o omitirse
            fechaEmision: fechaEmisionISO,
            fechaVencim: fechaVencimISO,
            nroTipoFormulario: pedidoVenta.nroTipoFormulario || '12',
            tipoCliente: pedidoVenta.tipoCliente || 'C',
            nroCliente: pedidoVenta.nroCliente || 0,
            nombre: pedidoVenta.nombre || '',
            nroVendedor: pedidoVenta.nroVendedor || 0,
            nroLista: pedidoVenta.nroLista || '',
            codigoMoneda: typeof pedidoVenta.codigoMoneda === 'number' 
                ? pedidoVenta.codigoMoneda 
                : (typeof pedidoVenta.codigoMoneda === 'string' && pedidoVenta.codigoMoneda === 'ARS' 
                    ? 0 
                    : Number(pedidoVenta.codigoMoneda) || 0),
            importeTotal: Math.round((pedidoVenta.importeTotal || 0) * 100) / 100, // Redondeado a 2 decimales
            asentado: pedidoVenta.asentado || 'N',
            descripcion: pedidoVenta.descripcion || '',
            usuario: pedidoVenta.usuario || '',
            nroSuc: (pedidoVenta as any).nroSuc || DEFAULT_NRO_SUC,
            detalles: detalles
        };

        // Incluir campos que vienen del pedido original (siempre incluir con valores por defecto)
        payload.letra = (pedidoVenta as any).letra || '';
        payload.nroComprobante = (pedidoVenta as any).nroComprobante ?? 0;
        payload.facturado = pedidoVenta.facturado || 'N';
        payload.nroFormularioFactura = pedidoVenta.nroFormularioFactura ?? 0;
        payload.integrar = pedidoVenta.integrar ?? 0;

        // Agregar campos nuevos (siempre incluir, incluso si son undefined)
        payload.nroUbicacionOrigen = nroUbicacionOrigen ?? 0;
        payload.ordenDeCompra = ((pedidoVenta as any).ordenCompra || (pedidoVenta as any).ordenDeCompra || '').trim();

        // Extraer nroExpreso de transporte (formato: "NOMBRE (número)" o solo el número como string)
        const transporteValue = (pedidoVenta as any).transporte || '';
        let nroExpreso: number = 0;
        if (transporteValue) {
            try {
                // Si viene en formato "NOMBRE (número)", extraer el número
                const match = String(transporteValue).match(/\((\d+)\)/);
                if (match && match[1]) {
                    nroExpreso = Number(match[1]);
                } else {
                    // Si no tiene formato, intentar convertir directamente a número
                    const numValue = Number(transporteValue);
                    if (!isNaN(numValue) && numValue > 0) {
                        nroExpreso = numValue;
                    }
                }
            } catch (error) {
                console.warn('Error al extraer nroExpreso:', error);
            }
        }
        payload.nroExpreso = nroExpreso;

        // Agregar formaDePago (siempre incluir, incluso si está vacío)
        payload.formaDePago = ((pedidoVenta as any).formaDePago || '').trim();

        // Agregar idCompania (solo si tiene valor válido)
        const idCompaniaValue = (pedidoVenta as any).idCompania;
        if (idCompaniaValue !== undefined && idCompaniaValue !== null && String(idCompaniaValue).trim() !== '') {
            payload.idCompania = String(idCompaniaValue).trim();
        }

        // Agregar campos opcionales (siempre incluir, incluso si son 0)
        payload.porcDesc = pedidoVenta.porcDesc !== undefined && pedidoVenta.porcDesc !== null 
            ? Number(pedidoVenta.porcDesc) 
            : 0;
        payload.porcDesc2 = pedidoVenta.porcDesc2 !== undefined && pedidoVenta.porcDesc2 !== null 
            ? Number(pedidoVenta.porcDesc2) 
            : 0;
        payload.porcDesc3 = pedidoVenta.porcDesc3 !== undefined && pedidoVenta.porcDesc3 !== null 
            ? Number(pedidoVenta.porcDesc3) 
            : 0;

        payload.tipoIva = String((pedidoVenta as any).tipoIVA ?? (pedidoVenta as any).tipoIva ?? '').trim() || undefined;
        payload.cuit = String((pedidoVenta as any).cuit ?? '').trim() || undefined;

        console.log('📤 PUT /api/pedidosventa/' + id, payload);
        console.log('📤 Campos incluidos:', {
            nroExpreso: payload.nroExpreso,
            formaDePago: payload.formaDePago,
            porcDesc: payload.porcDesc,
            porcDesc2: payload.porcDesc2,
            porcDesc3: payload.porcDesc3,
            ordenDeCompra: payload.ordenDeCompra,
            nroUbicacionOrigen: payload.nroUbicacionOrigen
        });

        const response = await api.put<PedidoVenta>(`/api/pedidosventa/${id}`, payload);
        return withPedidoNotasTrimmed(response.data);
    },

    deletePedidoVenta: async (id: number): Promise<void> => {
        await api.delete(`/api/pedidosventa/${id}`);
    },

    getPedidoVentaPDF: async (id: number): Promise<Blob> => {
        const response = await api.get(`/api/PedidosVenta/${id}/pdf`, {
            responseType: 'blob'
        });
        return response.data;
    },

    marcarImpreso: async (id: number): Promise<void> => {
        await api.post(`/api/PedidosVenta/${id}/marcar-impreso`);
    },

    marcarListoPicking: async (id: number): Promise<void> => {
        await api.post(`/api/PedidosVenta/${id}/marcar-listo-picking`);
    },

    marcarPreparado: async (id: number): Promise<void> => {
        await api.post(`/api/PedidosVenta/${id}/marcar-preparado`);
    },

    anularPedido: async (id: number): Promise<void> => {
        await api.post(`/api/PedidosVenta/${id}/anular`);
    }
};

export default pedidoVentaService; 